import { longApiDate, shortApiDate, type PlanPayday } from "@fomofi/shared";

import apiClient from "./client";
import type {
  ForecastAccentColor,
  ForecastInsight,
  ForecastMock,
  ForecastSummaryStat,
  ForecastTimelineEvent,
} from "@/data/mockForecast";

/* ─────────────────────── API response types (snake_case) ───────────────────────
 * Mirrors backend/apps/forecasting/serializers.py (ForecastPlanSerializer) from
 * GET /api/forecasting/plan/. Monetary values are JSON numbers (DRF FloatField);
 * dates are ISO (YYYY-MM-DD). The backend intentionally omits presentation
 * (labels, captions, allocation tagline/color) — the frontend owns those, keyed
 * by stable id/key (see the adapter). */

export interface ApiForecastPayPeriod {
  start: string; // ISO
  end: string; // ISO
  days_left: number;
  /** FP-062. Optional for a client deployed ahead of the API. */
  detected?: boolean;
}

export interface ApiForecastNextPaycheck {
  date: string; // ISO
  amount: number;
}

export interface ApiForecastBillsDue {
  total: number;
  due_by: string; // ISO
}

export interface ApiForecastAllocation {
  key: string;
  name: string;
  pct: number;
  /** Saved fixed-dollar intent. For percentage buckets this is today's amount. */
  amount: number;
  /** Amount that can be applied to today's surplus. Optional during rollout. */
  effective_amount?: number;
  /** True when today's surplus cannot fund the saved fixed-dollar intent. */
  over_allocated?: boolean;
  /** FP-037: set when the bucket holds a share but funds nothing ("no_debts": the
   *  Debt Payoff Fund on a user-owned split with no active debt). */
  unfunded_reason?: "no_debts" | null;
  spent: number;
  available: number;
  /** True while this percentage is still the split WE seeded, false once the
   *  user has adjusted it (BEN-141 S1). Optional for the deploy window only. */
  is_default?: boolean;
  color?: string;
  kind?: "spending" | "savings" | "debt" | "goal" | "custom";
  mode?: "percent" | "amount";
  /** Optional only while the backend/frontend deploy independently. */
  spent_source_parent?: "" | "needs" | "wants" | "savings";
  linked_goals?: Array<{
    id: number;
    name: string;
    current_amount: number;
    target_amount: number;
  }>;
}

export interface ApiAllocationRecommendation {
  reason: "goal_added" | "goal_removed" | "debt_added" | "debt_removed";
  allocations: Array<{ key: string; name: string; pct: number }>;
}

export interface ApiForecastAllocationsBasis {
  basis: string;
  basis_label: string;
  amount_qualifier: string;
}

/** Fallback for a backend that predates `allocations_basis` (BEN-141).
 *
 *  The web and API images used to deploy from SEPARATE workflows on the same
 *  merge, so for a window on every release either could be ahead. Since the
 *  2026-09-05 AWS cutover one workflow ships both to the same host, which
 *  narrows the window rather than closing it: the API container is replaced
 *  after the web image is in place. Without this, a frontend that lands first reads
 *  `api.allocations_basis.basis` off `undefined` and white-screens the Forecast
 *  page. Same copy as the server constants in forecasting/services/forecast_plan.py. */
const ALLOCATIONS_BASIS_FALLBACK: ApiForecastAllocationsBasis = {
  basis: "current_surplus_dual_mode",
  basis_label:
    "A one-time split of the surplus you have today. Percentage buckets keep their %; " +
    "fixed-dollar buckets keep their dollars as surplus changes.",
  amount_qualifier: "% of today's surplus",
};

export interface ApiForecastTimelineEvent {
  date: string; // ISO
  label: string;
  amount: number; // negative = outflow, positive = inflow, 0 = marker
  type: "bill" | "paycheck" | "marker";
  projected_balance: number | null;
  /** The stream's bucket (CAL-012). Optional: absent before the backend deploys. */
  parent_category?: string | null;
  /** The running balance after this event, markers included (BEN-141 Home).
   *  Optional for the deploy window. */
  balance_after?: number;
  /** CALL-06 decision 4: the stream this bill or paycheck is an occurrence of; null on
   *  markers. Optional for the deploy window. */
  recurring_id?: number | null;
}

export interface ApiForecastSummaryStat {
  id: string;
  amount: number;
  tone: "neutral" | "negative" | "positive";
  show_plus: boolean;
}

export interface ApiForecastInsight {
  type: ForecastInsight["type"];
  title: string;
  text: string;
}

export interface ApiForecastProjectedSpend {
  total: number;
  daily_rate: number;
  days_remaining: number;
  basis_days: number;
}

export interface ApiForecastPlan {
  pay_period: ApiForecastPayPeriod;
  next_paycheck: ApiForecastNextPaycheck | null;
  /** THE next payday: the main paycheck's (Paul, 2026-11-02). Absent on an older API. */
  next_payday?: PlanPayday | null;
  current_balance: number;
  bills_due_before_next_pay: ApiForecastBillsDue;
  projected_variable_spend: ApiForecastProjectedSpend;
  remaining_surplus: number;
  allocations: ApiForecastAllocation[];
  allocation_recommendation?: ApiAllocationRecommendation | null;
  /** Optional on the wire ONLY for the deploy window — see the fallback above. */
  allocations_basis?: ApiForecastAllocationsBasis;
  /** Contract DE09 step 2. Optional: an older API has none, and its buckets are
   *  still slices of `remaining_surplus`, so that is the fallback base. */
  period_remainder?: {
    available: boolean;
    reason: "no_income" | null;
    amount: number;
    base: number;
    cadence: string | null;
    monthly_income: number;
    monthly_bills: number;
  };
  timeline: ApiForecastTimelineEvent[];
  summary_stats: ApiForecastSummaryStat[];
  insights: ApiForecastInsight[];
  /** FP-041: null unless the Debt Eliminator uses a manual extra instead of the
   *  debt bucket. Optional for a client deployed ahead of the API. */
  debt_eliminator_extra?: { amount: number; cadence_label: string } | null;
}

export async function getForecastPlan(): Promise<ApiForecastPlan> {
  const { data } = await apiClient.get<ApiForecastPlan>("/forecasting/plan/");
  return data;
}

/** PATCH allocation percentages; backend returns the recomputed allocations. */
export interface AllocationUpdate {
  key: string;
  mode?: "percent" | "amount";
  pct?: number;
  amount?: number;
}

export async function updateForecastAllocations(
  updates: AllocationUpdate[],
): Promise<ApiForecastAllocation[]> {
  const { data } = await apiClient.patch<ApiForecastAllocation[]>(
    "/forecasting/plan/allocations/",
    {
      allocations: updates.map((u) => ({
        key: u.key,
        mode: u.mode,
        pct: u.pct?.toFixed(2),
        amount: u.amount?.toFixed(2),
      })),
    },
  );
  return data;
}

export interface AllocationCreate {
  name: string;
  color: string;
  kind: NonNullable<ApiForecastAllocation["kind"]>;
  mode: NonNullable<ApiForecastAllocation["mode"]>;
  pct?: number;
  amount?: number;
  spent_source_parent?: "" | "needs" | "wants" | "savings";
}

export type AllocationEdit = Partial<AllocationCreate>;

export async function createForecastAllocation(
  input: AllocationCreate,
): Promise<ApiForecastAllocation[]> {
  const { data } = await apiClient.post<ApiForecastAllocation[]>(
    "/forecasting/plan/allocations/",
    input,
  );
  return data;
}

export async function editForecastAllocation(
  key: string,
  input: AllocationEdit,
): Promise<ApiForecastAllocation[]> {
  const { data } = await apiClient.patch<ApiForecastAllocation[]>(
    `/forecasting/plan/allocations/${key}/`,
    input,
  );
  return data;
}

export async function deleteForecastAllocation(key: string): Promise<ApiForecastAllocation[]> {
  const { data } = await apiClient.delete<ApiForecastAllocation[]>(
    `/forecasting/plan/allocations/${key}/`,
  );
  return data;
}

export async function applyAllocationRecommendation(): Promise<ApiForecastAllocation[]> {
  const { data } = await apiClient.post<ApiForecastAllocation[]>(
    "/forecasting/plan/allocations/recommendation/apply/",
  );
  return data;
}

export async function dismissAllocationRecommendation(): Promise<void> {
  await apiClient.delete("/forecasting/plan/allocations/recommendation/");
}

/* ───────────────────────────────── adapter ─────────────────────────────────
 * Maps the lean API response onto the UI's camelCase `ForecastMock` shape,
 * formatting ISO dates into the display strings the components expect and
 * supplying the presentation the backend omits (allocation tagline/color,
 * summary-stat labels/captions). Components keep their JSX unchanged. */

// Date formatting lives in `@fomofi/shared` so the paycheck adapter renders the
// same paycheck the same way (BEN-141 2f), and so web and native cannot drift
// from each other either (BEN-141 P20, #744). Local aliases keep this file's
// call sites unchanged.
const shortDate = shortApiDate;
const longDate = longApiDate;

// Allocation presentation, keyed by the stable backend `key` (matches the mock).
const ALLOCATION_PRESENTATION: Record<string, { tagline: string; color: ForecastAccentColor }> = {
  splash: { tagline: "Freedom to enjoy life ✨", color: "orange" },
  groceries: { tagline: "Feed your life 🥑", color: "blue" },
  emergency: { tagline: "Peace of mind first 🛡️", color: "purple" },
  savings: { tagline: "Future you will thank you 💝", color: "purple" },
  debt: { tagline: "Crush debt. Build freedom.", color: "red" },
};
const ALLOCATION_FALLBACK = { tagline: "", color: "blue" as ForecastAccentColor };
const FORECAST_COLORS = new Set<ForecastAccentColor>([
  "orange",
  "green",
  "emerald",
  "purple",
  "red",
  "blue",
]);

// Summary-stat display labels, keyed by the stable backend `id`.
// The `current-balance` id is the backend's and stays as-is; only the DISPLAY
// string changes, to the spendable label this figure has always deserved
// (forecast_plan sources it from banking.services.spendable_balance) — BEN-141 D2.
const STAT_LABELS: Record<string, string> = {
  "current-balance": "Available to Spend",
  // One wording for one figure (vocabulary classification, Bills Due Before Next
  // Paycheck): the Dashboard and Calendar print the same number under it.
  "bills-due": "Bills Due Before Next Paycheck",
  surplus: "Remaining Surplus Now",
  "next-paycheck": "Next Paycheck",
  "upcoming-bills": "Upcoming Bills After Paycheck",
  "projected-balance": "Remaining After Upcoming Bills",
};

function adaptTimeline(events: ApiForecastTimelineEvent[]): ForecastTimelineEvent[] {
  return events.map((e) => ({
    date: shortDate(e.date),
    // Preserved, not derived-and-discarded. See ForecastTimelineEvent.dateISO.
    dateISO: e.date,
    label: e.label,
    amount: e.amount,
    type: e.type,
    projectedBalance: e.projected_balance ?? undefined,
    parentCategory: e.parent_category ?? null,
    balanceAfter: e.balance_after ?? null,
    recurringId: e.recurring_id ?? null,
  }));
}

/** Exported for report 145: the timeline swaps it for "Can't reach your bank" when that's why. */
export const NO_PAYCHECK_CAPTION = "No paycheck detected yet";

function adaptSummaryStats(
  stats: ApiForecastSummaryStat[],
  paycheck: ApiForecastNextPaycheck | null,
  billsDueBy: string,
  timeline: ApiForecastTimelineEvent[],
): ForecastSummaryStat[] {
  // Period-2 window for the "upcoming bills" caption: next paycheck → last marker.
  const lastMarker = [...timeline].reverse().find((e) => e.type === "marker");
  const captionFor = (id: string): string | undefined => {
    switch (id) {
      case "bills-due":
        return `Due by ${shortDate(billsDueBy)}`;
      // BEN-141 B3. These two shared the caption "Available to allocate" under
      // two differently-titled cards carrying two different figures ($3,895.04
      // and $3,947.03 on the demo persona). Only the first is allocatable and
      // only the first is measured on today.
      //
      // The difference between them is NOT "bills", which is why neither caption
      // says so. `surplus` is balance − this period's remaining bills − the
      // projected everyday spend. `projected-balance` is the running balance at
      // the LAST timeline marker: it adds the next paycheck, subtracts period-2
      // bills, and — see the PR — never subtracts an everyday-spend projection at
      // all, which is why the "later" figure comes out HIGHER than the "now" one.
      // So each caption states its own horizon and the second does not claim to
      // be allocatable.
      // ForecastEquationRow's equation card prints the SAME figure (both are
      // `remaining_surplus`), so it has to print the same words: one figure, one
      // caption. Different figure, different caption — see `projected-balance`.
      // Contract batch 21: it read 'Available to allocate today', which stopped
      // being true when batch 17 moved the buckets onto each paycheck's remainder.
      case "surplus":
        // FP-062: the same words as ForecastEquationRow's card for the same figure.
        return paycheck ? "Left until your next paycheck" : `Left by ${shortDate(billsDueBy)}`;
      case "projected-balance":
        return lastMarker
          ? `Projected balance on ${shortDate(lastMarker.date)}`
          : "Projected balance";
      case "upcoming-bills":
        if (!paycheck) return NO_PAYCHECK_CAPTION;
        return lastMarker
          ? `${shortDate(paycheck.date)} – ${shortDate(lastMarker.date)}`
          : undefined;
      // FP-062 / D4: with no paycheck these two read $0.00, and a $0 carries its reason.
      case "next-paycheck":
        return paycheck ? undefined : NO_PAYCHECK_CAPTION;
      default:
        return undefined;
    }
  };
  const labelFor = (id: string): string => {
    const base = STAT_LABELS[id] ?? id;
    // The mock annotates next-paycheck's label with its date, e.g. "Next Paycheck (May 16)".
    if (id === "next-paycheck" && paycheck) return `${base} (${shortDate(paycheck.date)})`;
    // #1248's equation-card rule, applied to the summary card beside it: an
    // account with no income stream has no next pay period to name.
    if (id === "bills-due" && !paycheck) return "Bills Due In This Window";
    return base;
  };
  return stats.map((s) => ({
    id: s.id,
    label: labelFor(s.id),
    amount: s.amount,
    caption: captionFor(s.id),
    tone: s.tone,
    showPlus: s.show_plus,
  }));
}

export function toForecastPlan(api: ApiForecastPlan): ForecastMock {
  return {
    payPeriod: {
      start: shortDate(api.pay_period.start),
      end: shortDate(api.pay_period.end),
      daysLeft: api.pay_period.days_left,
      detected: api.pay_period.detected ?? true,
      startISO: api.pay_period.start,
      endISO: api.pay_period.end,
    },
    nextPaycheck: {
      // When there is no income stream the backend returns null; the header
      // falls back to a dash. Keep a stable shape so components stay simple.
      date: api.next_paycheck ? longDate(api.next_paycheck.date) : "—",
      amount: api.next_paycheck?.amount ?? 0,
      dateISO: api.next_paycheck?.date,
    },
    // The stable shape above cannot answer "is there a paycheck at all" — "—"
    // and 0 are both legitimate values — and the equation row needs to, so it
    // does not title a card after a pay period this account does not have
    // (BEN-141 #1248). Carried as a boolean rather than re-derived from the dash.
    hasNextPaycheck: api.next_paycheck !== null,
    nextPayday: api.next_payday ?? null,
    debtEliminatorExtra: api.debt_eliminator_extra
      ? {
          amount: api.debt_eliminator_extra.amount,
          cadenceLabel: api.debt_eliminator_extra.cadence_label,
        }
      : null,
    currentBalance: api.current_balance,
    billsDueBeforeNextPay: {
      total: api.bills_due_before_next_pay.total,
      dueBy: shortDate(api.bills_due_before_next_pay.due_by),
      dueByISO: api.bills_due_before_next_pay.due_by,
    },
    projectedVariableSpend: {
      total: api.projected_variable_spend.total,
      dailyRate: api.projected_variable_spend.daily_rate,
      daysRemaining: api.projected_variable_spend.days_remaining,
      basisDays: api.projected_variable_spend.basis_days,
    },
    remainingSurplus: api.remaining_surplus,
    allocations: api.allocations.map((a) => {
      const pres = ALLOCATION_PRESENTATION[a.key] ?? ALLOCATION_FALLBACK;
      return {
        id: a.key,
        name: a.name,
        tagline: pres.tagline,
        pct: a.pct,
        amount: a.amount,
        effectiveAmount: a.effective_amount ?? a.amount,
        overAllocated: a.over_allocated ?? false,
        unfundedReason: a.unfunded_reason ?? null,
        spent: a.spent,
        available: a.available,
        color: FORECAST_COLORS.has(a.color as ForecastAccentColor)
          ? (a.color as ForecastAccentColor)
          : pres.color,
        // Absent on a backend that predates the field → treat as the user's, so
        // an old API can never accuse someone of not having chosen their own split.
        isDefault: a.is_default ?? false,
        kind: a.kind ?? "custom",
        mode: a.mode ?? "percent",
        spentSourceParent: a.spent_source_parent ?? "",
        linkedGoals: (a.linked_goals ?? []).map((goal) => ({
          id: goal.id,
          name: goal.name,
          currentAmount: goal.current_amount,
          targetAmount: goal.target_amount,
        })),
      };
    }),
    allocationRecommendation: api.allocation_recommendation
      ? {
          reason: api.allocation_recommendation.reason,
          allocations: api.allocation_recommendation.allocations.map((row) => ({
            id: row.key,
            name: row.name,
            pct: row.pct,
          })),
        }
      : null,
    periodRemainder: api.period_remainder
      ? { available: api.period_remainder.available, base: api.period_remainder.base }
      : { available: true, base: api.remaining_surplus },
    allocationsBasis: (() => {
      const b = api.allocations_basis ?? ALLOCATIONS_BASIS_FALLBACK;
      return {
        basis: b.basis,
        basisLabel: b.basis_label,
        amountQualifier: b.amount_qualifier,
      };
    })(),
    timeline: adaptTimeline(api.timeline),
    summaryStats: adaptSummaryStats(
      api.summary_stats,
      api.next_paycheck,
      api.bills_due_before_next_pay.due_by,
      api.timeline,
    ),
    insights: api.insights.map((i) => ({ type: i.type, title: i.title, text: i.text })),
  };
}
